/*Copyright (c) 2025 por Tecnologías de Información MALMUR y empresas afiliadas. Todos los derechos reservados.
Este software es información confidencial y propietaria de Copyright (c) 2025 por Tecnologías de Información MALMUR
 y sus afiliadas. ("Información Confidencial"). Usted no puede divulgar dicha Información Confidencial, 
 y solo puede usar dicha Información Confidencial de acuerdo con los términos del acuerdo de licencia que celebró con MALMUR.*/

/*Copyright (c) 2025 by Tecnologías de Información MALMUR and affiliated companies. All Rights Reserved.
This software is confidential and proprietary information of Copyright (c) 2025 by Tecnologías de Información 
MALMUR and its affiliates. ("Confidential Information"). You may not disclose such Confidential Information, 
and may only use such Confidential Information in accordance with the terms of the license agreement you entered 
into with MALMUR.*/



import { PatientInterface, HeaderEnum, AccountingInterface } from "@malmur-technologies/medicalsystem-shared";
import useHttp from "hooks/http.hook";
import Button from "components/generals/Button";
import Box from "components/generals/Box";
import AppointmentWidget from "components/widgets/AppointmentWidget";
import Card from "components/generals/Card";
import { Grid, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { CoreAPI_URLs } from "constants/http.const";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

//TODO: REMOVE MOCK DATA
/**
 * Props for PatientHubForm component.
 */
interface PatientHubFormInterface {
  patientModel: PatientInterface;
}

/**
 * Patient hub summary form displaying key patient information widgets.
 * 
 * Features:
 * - Appointment widget showing upcoming and past appointments
 * - Insurance information table
 * - Responsive grid layout (2 columns on desktop, stacked on mobile)
 * - Automatic data fetching on component mount
 * - Empty state handling with informative messages
 * - Integration with patient insurance API
 * 
 * Displays:
 * - Patient appointment history and scheduling
 * - Active insurance policies and coverage details
 * - Quick access to patient-related actions
 * 
 * @param patientModel - Patient data to display in hub widgets
 * @returns Patient hub form with appointment and insurance widgets
 */
const PatientHubForm: React.FC<PatientHubFormInterface> = ({ patientModel }) => {
  const [,,sendRequest] = useHttp();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [accountingResponse, setAccountingResponse] = useState<AccountingInterface>();

  useEffect(() => {
    fetchAccount().then((accounting) => {
      setAccountingResponse(accounting || undefined);
    });
  }, []);

  const openMovementsHandler = (accounting: AccountingInterface) => {
    navigate(`/accountingMovements`, {
      state: { accountingId: accounting.id, patient: accounting.patient },
    });
  };

  
  const fetchAccount = async (): Promise<AccountingInterface | null> => {
    try {
      const response = await sendRequest<{ results: AccountingInterface }>(
        CoreAPI_URLs.ACCOUNTING_BY_PATIENT_ID,
        (res) => res,
        "GET",
        null,
        [{ header: HeaderEnum.RESOURCE_ID, value: patientModel.id }]
      );
      const accounting = response?.data?.results || response?.data;
      return accounting || null;
    } catch (error: any) {
      if (error?.response?.status === 404) {
        return null;
      }
      throw error;
    }
  };

  return (
    <>
      {patientModel ? (
<Box mt={2} mb={2} width="100%">
  <Grid container spacing={4} alignItems="stretch">
    <Grid item xs={12} md={6} style={{ display: 'flex' }}> 
        <AppointmentWidget patientSelected={patientModel} />
    </Grid>
    <Grid item xs={12} md={6} style={{ display: 'flex' }}>
      <Card id="2" className="card-base" style={{ width: '100%' }}>
        {accountingResponse ? (
          <>
          <Typography variant="h6" className="patient-hub-form__title">{t("patientHubForm.accountingMovements")}</Typography>
          <Typography variant="caption" mb={1}>{t("patientHubForm.accountingMovementsdescription")}</Typography>
              <Button
                id="cy-view-accounting"
                variant="contained"
                color="info"
                onClick={() => openMovementsHandler(accountingResponse)}      
              >
                {t("patientHubForm.viewDetails")}
              </Button>   

          <Typography variant="h6" mt={2} className="patient-hub-form__title">{t("patientHubForm.payments")}</Typography>
          <Typography variant="caption" mb={1}>{t("patientHubForm.paymentsdescription")}</Typography>
              <Button
                id="cy-view-payments"
                variant="contained"
                color="info"
                onClick={() => navigate(`/payment?accountingId=${accountingResponse.id}`)}      
              >
                {t("patientHubForm.viewPayments")}
              </Button> 
          </>
        ) : (
          <>
         <Typography variant="h6" fontWeight="bold" className="patient-hub-form__no-info">{t("patientHubForm.noInformationAvailable")}</Typography>
          </>
        )}
      </Card>
    </Grid>
  </Grid>
</Box>
      ) : (
        <Typography variant="body2" className="text-base font-bold">{t("patientHubForm.noInformationAvailable")}</Typography>
      )}
    </>
  );
};

export default PatientHubForm;
